import { Injectable } from "@angular/core";
import { AuthenticationMethod, IntegrationModel, Platform, ResourceType } from "src/app/models/api";
import { ActionTypes } from "src/app/models/api/playbooks/actions/actionTypes.enum";
import { PlaybookSupportedActions } from "src/app/models/componentInputs/playbookActionInput.interface";
import { LabelsProviderPipe } from "src/app/pipes/labels-provider/labels-provider.pipe";
import { IActionTypeResponse } from "src/app/services/httpServices/actionsHttpService/actions-http-service.service";
import { LoggerService } from "src/app/services/infra/logger/logger.service";
import { PermissionsService } from "src/app/services/infra/permissionsService/permissions.service";
import { arePlatformActionsAllowed } from "../../common/actionsHelpers";
import { PlaybookTrigger } from "src/app/models/api/playbooks/playbook.model";
import { TriggerTypes } from "src/app/models/api/playbooks/triggers/triggerTypes.enum";
import { NewViolationFoundTriggerConfigModel } from "src/app/models/api/playbooks/triggers/newViolationTriggerModel";
import { NewResourceFoundTriggerConfigModel } from "src/app/models/api/playbooks/triggers/newResourceTriggerModel";
import { StoreService } from "src/app/services/infra/store/store.service";
import { BaseViolationRule } from "src/app/models/api/governance/violationRule.model";
import _ from "lodash";

@Injectable({
    providedIn: "root",
})
export class PlaybooksTriggerActionsService {
    private readonly _generalActions = [ActionTypes.wait, ActionTypes.sendEmail, ActionTypes.sendWebhook];
    private _baseViolationRules: BaseViolationRule[] = [];

    constructor(
        private _store: StoreService,
        private _permissionsService: PermissionsService,
        private _labelsProvider: LabelsProviderPipe,
        private _logger: LoggerService,
    ) {
        this._store.baseViolationRule$.subscribe((rules) => (this._baseViolationRules = rules));
    }

    public setActionsDisabledAndTooltip(integration: IntegrationModel, actions: IActionTypeResponse[]): Set<PlaybookSupportedActions> {
        const supportedActions: PlaybookSupportedActions[] = [];
        actions.forEach((action) => {
            const actionType = action.actionType;
            const tooltip = this._getActionTooltip(actionType, action.restrictedReason);
            const supportedAction: PlaybookSupportedActions = {
                actionType: actionType,
                disabled: action.isRestricted,
                tooltip: tooltip !== "" ? tooltip : undefined,
            };
            if (integration.platform == Platform.workato) {
                this._handleWorkatoActions(supportedAction, integration.authenticationMethod);
            }
            supportedActions.push(supportedAction);
        });
        return new Set(supportedActions);
    }

    public getTriggerResourceTypes(trigger: PlaybookTrigger): Set<ResourceType> {
        if (trigger.triggerType == TriggerTypes.newResourceFound) {
            const configuration = trigger.configuration as NewResourceFoundTriggerConfigModel;
            if (configuration.resourceTypes) {
                return new Set(configuration.resourceTypes);
            } else return new Set();
        } else if (trigger.triggerType == TriggerTypes.newViolationFound) {
            const configuration = trigger.configuration as NewViolationFoundTriggerConfigModel;
            const foundResourceTypes: Set<ResourceType> = new Set<ResourceType>();
            if (configuration.ruleIds) {
                const baseViolationRulesById = _.keyBy(this._baseViolationRules, "id");
                const rulesIdsNotInBaseViolationRules = configuration.ruleIds.filter((ruleId) => !baseViolationRulesById[ruleId]);
                if (rulesIdsNotInBaseViolationRules.length > 0) {
                    this._logger.error(`Playbook contains deleted rules ${rulesIdsNotInBaseViolationRules.join(", ")}`);
                }

                configuration.ruleIds
                    .filter((ruleId) => baseViolationRulesById[ruleId])
                    .forEach((ruleId) => {
                        const rule = baseViolationRulesById[ruleId];
                        foundResourceTypes.add(rule.resourceType);
                    });
                return foundResourceTypes;
            } else return foundResourceTypes;
        }
        throw new Error(`PlaybooksTriggerActionsService: Missing support for ${trigger.triggerType} trigger`);
    }

    public getRulesByIds(ruleIds: Set<string>): BaseViolationRule[] {
        return this._baseViolationRules.filter((rule) => ruleIds.has(rule.id));
    }

    private _getActionTooltip(actionType: ActionTypes, restrictedReason: string): string {
        switch (actionType) {
            case ActionTypes.wait:
                return this._labelsProvider.transform("playbooks.waitActionTooltip");
        }
        return restrictedReason;
    }

    private _handleWorkatoActions(supportedAction: PlaybookSupportedActions, authMethod: AuthenticationMethod): void {
        if (this._generalActions.includes(supportedAction.actionType)) return;
        const workatoActionsAllowed = arePlatformActionsAllowed(Platform.workato, authMethod);
        const disabled = !workatoActionsAllowed || supportedAction.disabled ? true : false;
        const tooltip = !workatoActionsAllowed ? this._labelsProvider.transform("playbooks.apiAction") : supportedAction.tooltip;
        supportedAction.disabled = disabled;
        supportedAction.tooltip = tooltip;
    }
}
